import type { DocumentLink } from '@heybesafe/shared';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';

interface Props {
  values: DocumentLink[];
  onChange: (next: DocumentLink[]) => void;
}

/**
 * A repeatable list of a document's related web sites — each an optional label
 * + a URL. Rows with a blank URL are the caller's to drop on save (mirrors
 * ContactFieldListField / CustomFieldListField).
 *
 * User-facing copy says "Related Web Sites"; the underlying model keeps the
 * shorter `links` / `DocumentLink` naming, which is shared with the other
 * clients and the stored item shape.
 */
export default function DocumentLinksField({ values, onChange }: Props): JSX.Element {
  function updateAt(i: number, next: DocumentLink): void {
    const copy = [...values];
    copy[i] = next;
    onChange(copy);
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-medium text-hbs-text-mute uppercase tracking-hbs-overline">
        Related Web Sites
      </legend>
      {values.map((link, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="w-[130px] flex-shrink-0">
            <input
              type="text"
              aria-label={`Related web site label ${i + 1}`}
              value={link.label ?? ''}
              placeholder="Label (optional)"
              onChange={(e) => updateAt(i, { ...link, label: e.target.value })}
              className="w-full h-12 px-3 bg-hbs-surface border-[1.5px] border-hbs-border rounded-hbs-sm text-[15px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary focus-within:ring-4 focus-within:ring-hbs-primary/20"
            />
          </div>
          <div className="flex-1">
            <HbsInput
              type="text"
              value={link.url}
              placeholder="https://example.com"
              onChange={(url) => updateAt(i, { ...link, url })}
            />
          </div>
          <button
            id={`document-link-remove-${i}`}
            type="button"
            aria-label={link.label ? `Remove ${link.label}` : `Remove related web site ${i + 1}`}
            onClick={() => onChange(values.filter((_, j) => j !== i))}
            className="shrink-0 mt-3.5 p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
          >
            ×
          </button>
        </div>
      ))}
      <HbsButton id="document-link-add-button" variant="ghost" type="button" onClick={() => onChange([...values, { url: '' }])}>
        + Add web site
      </HbsButton>
    </fieldset>
  );
}
